import React, { useEffect, useRef, useState } from 'react';

import { decodeJwt } from '../utils/jwt';
import { ACCESS_TOKEN_ENDPOINT, LOGIN_ENDPOINT } from '../constants';
import { removeFromLocalStorage, setValueToLocalStorage } from '../utils/localStorage';
import { getAuthClientId, getBaseUrl } from '../../constant';

const GOOGLE_BUTTON_ID = 'google-signin';
const DEFAULT_TENANT_NAME = 'zopsmart';

const getSelectedTenantId = (tenants = [], tenantID = '') => {
  if (tenantID) return tenantID;

  const zopsmartTenant = tenants.find((tenant) => {
    const tenantName = tenant?.name || '';
    return tenantName.toLowerCase() === DEFAULT_TENANT_NAME;
  });

  return zopsmartTenant?.id || tenants?.[0]?.id || '';
};

export default function LoginAuth({
  onSuccess,
  redirectUrl,
  loginEndpoint = LOGIN_ENDPOINT,
  tenantID = '',
}) {
  const [authError, setAuthError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const buttonContainerRef = useRef(null);
  const AUTH_CLIENT_ID = getAuthClientId();

  const handleAuthSuccess = (accessToken, refreshToken = null, tenantID = null) => {
    const decoded = decodeJwt(accessToken);
    if (!decoded) {
      setAuthError('Failed to decode JWT');
      return;
    }

    const userInfo = { ...decoded, accessToken };
    setValueToLocalStorage('googleToken', accessToken);
    if (refreshToken) setValueToLocalStorage('refreshToken', refreshToken);
    if (tenantID) setValueToLocalStorage('tenantID', tenantID);
    removeFromLocalStorage('loggedInUserInfo');
    if (onSuccess) {
      onSuccess(userInfo, redirectUrl);
    }
  };

  const refreshAccessToken = async (refreshToken, selectedTenantId) => {
    const response = await fetch(`${getBaseUrl()}${ACCESS_TOKEN_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refreshToken,
        tenantID: selectedTenantId,
      }),
    });
    const refreshData = await response.json();
    if (!response.ok || !refreshData?.data?.accessToken) {
      throw new Error(refreshData?.errors?.[0]?.reason || 'Refresh api failed');
    }
    return refreshData.data.accessToken;
  };

  const loginWithGoogle = async (googleCredential) => {
    const response = await fetch(`${getBaseUrl()}${loginEndpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        provider: 'GOOGLE',
        providerData: {
          token: googleCredential,
        },
        platform: 'WEB',
      }),
    });
    const loginData = await response.json();
    if (!response.ok || !loginData?.data) {
      throw new Error(loginData?.errors?.[0]?.reason || 'login failed');
    }
    return loginData.data;
  };

  useEffect(() => {
    if (!AUTH_CLIENT_ID) {
      setAuthError('AUTH_CLIENT_ID is not defined');
      return undefined;
    }

    let isMounted = true;
    let renderTimer = null;

    const renderGoogleButton = () => {
      if (!isMounted) return;
      if (!window.google?.accounts?.id || !buttonContainerRef.current) return;

      buttonContainerRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: AUTH_CLIENT_ID,
        callback: async (response) => {
          try {
            setIsLoading(true);
            setAuthError(null);

            const googleCredential = response?.credential;
            if (!googleCredential) {
              throw new Error('Invalid credential response: No access token');
            }

            const loginData = await loginWithGoogle(googleCredential);
            const selectedTenantId = getSelectedTenantId(loginData.tenants, tenantID);
            const accessToken =
              loginData.accessToken ||
              (await refreshAccessToken(loginData.refreshToken, selectedTenantId));

            if (!isMounted) return;
            handleAuthSuccess(accessToken, loginData.refreshToken, selectedTenantId);
          } catch (error) {
            if (isMounted) {
              setAuthError(error?.message || 'Authentication failed');
            }
          } finally {
            if (isMounted) {
              setIsLoading(false);
            }
          }
        },
      });
      window.google.accounts.id.renderButton(buttonContainerRef.current, {
        theme: 'filled_blue',
        size: 'large',
        type: 'standard',
        width: window.innerWidth <= 960 ? 200 : 300,
        text: 'signin_with',
        logo_alignment: 'left',
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
    } else {
      renderTimer = window.setInterval(() => {
        if (window.google?.accounts?.id) {
          window.clearInterval(renderTimer);
          renderGoogleButton();
        }
      }, 300);
    }

    return () => {
      isMounted = false;
      if (renderTimer) {
        window.clearInterval(renderTimer);
      }
    };
  }, [AUTH_CLIENT_ID, loginEndpoint, redirectUrl, tenantID]);

  if (!AUTH_CLIENT_ID) {
    return <div>Error: AUTH_CLIENT_ID is not defined</div>;
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <h3>Login with Google</h3>
      {authError && <div style={{ color: 'red', marginBottom: '10px' }}>{authError}</div>}
      <div
        id={GOOGLE_BUTTON_ID}
        ref={buttonContainerRef}
        style={{ display: 'inline-block', minHeight: '44px' }}
      />
      {isLoading && <div style={{ marginTop: '10px' }}>Signing you in...</div>}
    </div>
  );
}
